import React from "react";
import cn from "classnames";
import { useMutation, useQueryClient } from "react-query";

import { Picture } from "../Picture";
import { PopConfirm } from "../PopConfirm";
import { toastService } from "../../services/toastService";
import { apiErrorService } from "../../services/apiErrorService";
import { AppError } from "../../types/Error";
import { BrigadeFull } from "../../types/brigade";
import { useNavigate } from "react-router-dom";
import { useSelectedBrigade } from "../../hooks/brigades/use-selected-brigade";
import { adminService } from "../../services/adminService";

type Props = {
  brigade: BrigadeFull;
};

export const BrigadeItem: React.FC<Props> = React.memo(({ brigade }) => {
  const navigate = useNavigate();
  const selectedBrigade = useSelectedBrigade();
  const queryClient = useQueryClient();

  const { mutate: remove, isLoading } = useMutation<void, AppError, number>({
    mutationFn: async (id) => {
      await adminService.removeBrigade(id);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["brigades"] });
      toastService.success({ title: "Подію видалено успішно" });
    },
    onError: (error) => {
      const message = apiErrorService.getMessage(
        error,
        "Не вдалось видалити подію",
      );

      toastService.error(message);
    },
    onMutate: () => toastService.dismiss(),
  });

  return (
    <div
      role="button"
      className={cn(
        "row d-flex btn btn-light w-100 m-0 mb-3 py-2 px-1",
        "rounded border border-2 position-relative",
        {
          "pe-none opacity-50": isLoading,
        },
      )}
      style={{
        backgroundColor: selectedBrigade?.id === brigade.id ? "#bfbfbf" : "",
      }}
      onClick={() => navigate(`/admin/brigades/${brigade.id}`)}
      onKeyDown={() => {}}
      tabIndex={0}
    >
      <div className="d-block d-md-none d-lg-block col-1 p-0 p-lg-2">
        <Picture className="table-image rounded-circle" src={brigade.image} />
      </div>

      <div className="ms-3 ms-md-0 col-7">
        <p className="text-start">{brigade.name}</p>
      </div>

      <div className="col-2 d-flex align-items-center flex-row">
        <span>Героїв: {brigade.victimsCount}</span>
      </div>

      <div className="col-2 d-flex justify-content-end align-items-center">
        <PopConfirm onConfirm={() => remove(brigade.id)}>
          {(togglePopover) => (
            <button
              type="button"
              className="btn btn-secondary p-1 ms-3"
              onClick={(event) => {
                event.stopPropagation();
                togglePopover();
              }}
            >
              <i className="bi bi-x-circle fs-3 lh-1 align-middle" />
            </button>
          )}
        </PopConfirm>
      </div>

      {isLoading && (
        <div
          className="position-absolute top-0 bottom-0 start-0 end-0
          d-flex align-items-center justify-content-center"
        >
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}
    </div>
  );
});
